import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import moment from 'moment';
import { Typography } from '@mui/material';
import TaxDocumentsTable from './TaxDocumentsTable';
import PagePaper from '../../components/Common/PagePaper';
import useActiveUser from '../../hooks/useActiveUser';
import useHasActiveFeatureFlag from '../../hooks/useFeatureFlags';
import { FF_TAX_DOCUMENTS } from '../../components/FeatureFlagWrapper';
import * as authService from '../../services/auth';
import { Roles } from '../../models/roles';
import routes from '../../routes';
import { downloadTaxDocument, fetchTaxDocuments } from '../../actions/taxDocuments';

const formatReportingPeriod = (start, end) =>
  `${moment(start, 'YYYY-MM-DD').format('MM/DD/YYYY')} - ${moment(end, 'YYYY-MM-DD').format('MM/DD/YYYY')}`;

// Same split as DonorDashboardContainer: umbrella roles carry a donor_id covering every location,
// location roles carry a single donor_location_id. Reading them off the impersonating user is what
// makes an Admin switched into a Food Donor see that donor's documents.
const getFoodDonorScope = (activeUser) => {
  if (!activeUser) {
    return null;
  }

  const userRoles = authService.getUserRolesInCurrentlySelectedSite(activeUser);
  const umbrellaRole = userRoles.find((role) => [Roles.DonorAdmin, Roles.DonorStaff].indexOf(role.role_name) > -1);

  if (umbrellaRole) {
    return { donor_id: umbrellaRole.donor_id };
  }

  const locationRole = userRoles.find(
    (role) => [Roles.DonorLocationAdmin, Roles.DonorLocationStaff].indexOf(role.role_name) > -1
  );

  if (locationRole) {
    return { food_donor_id: locationRole.donor_location_id };
  }

  return null;
};

const TaxDocumentsView = () => {
  const dispatch = useDispatch();
  const activeUser = useActiveUser();
  const hasTaxDocumentsFF = useHasActiveFeatureFlag(FF_TAX_DOCUMENTS);
  const taxDocumentsEntities = useSelector((state) => state.entities.taxDocuments.byId);
  const inflight = useSelector((state) => state.entities.taxDocuments.inflight);
  const lastUpdated = useSelector((state) => state.entities.taxDocuments.lastUpdated);
  const foodDonorScope = getFoodDonorScope(activeUser);
  // treat "not fetched yet" as loading so the empty state never flashes before the first response
  const isLoading = inflight || lastUpdated === null;

  useEffect(() => {
    if (hasTaxDocumentsFF && foodDonorScope) {
      dispatch(fetchTaxDocuments(foodDonorScope));
    }
  }, [dispatch, hasTaxDocumentsFF, activeUser && activeUser.id, foodDonorScope && JSON.stringify(foodDonorScope)]);

  if (!hasTaxDocumentsFF) {
    return <Redirect to={routes.dashboardFoodDonor} />;
  }

  const taxDocumentsList = Object.values(taxDocumentsEntities).map((taxDocument) => ({
    ...taxDocument,
    reporting_period: formatReportingPeriod(taxDocument.reporting_period_start, taxDocument.reporting_period_end),
  }));

  const onDownloadClick = (taxDocument) => dispatch(downloadTaxDocument(taxDocument));

  return (
    <div>
      <PagePaper>
        <Typography variant="h5">Tax Certification Documents</Typography>
        <Typography variant="body2" color="textSecondary">
          Official year-end tax substantiation letters generated from your donation records.
        </Typography>
      </PagePaper>

      {!isLoading && taxDocumentsList.length === 0 ? (
        <PagePaper>
          <Typography variant="subtitle1" color="textSecondary">
            No tax documents are available yet.
          </Typography>
        </PagePaper>
      ) : (
        <TaxDocumentsTable taxDocuments={taxDocumentsList} isLoading={isLoading} onDownloadClick={onDownloadClick} />
      )}
    </div>
  );
};

export default TaxDocumentsView;
